import {
  BaseChart,
  LineSeries,
  ScadaDatetimeTagDataSource,
  ScadaDatetimeTagArgs,
  YAxis,
  ScadaMedianDataSource,
  ScadaRangeDataSource,
  RangeSeries,
  getColorIndex,
  isScadaDateTimeTagDataSource,
  Series,
  DataSource,
  ReferenceLine,
  Axis,
  RawDataSource
} from "interfaces/chart";
import { ChartSubtype } from "components/DataExplorationV2/ChartSubtypes";
import { Dispatch, useEffect, useReducer } from "react";
import { v4 as createUUID } from "uuid";
import { COLOR_PALETTE } from "core/chart-mappers/aihub-chart-to-highcharts";
import { removeFirstOccurrence } from "core/utils/array";

export type DataExplorationChart = BaseChart<DataExplorationSeries>;

export type DataExplorationSeries =
  | LineSeries<
      ScadaDatetimeTagDataSource | ScadaMedianDataSource | RawDataSource
    >
  | RangeSeries<ScadaRangeDataSource>;

//region Actions types
type ChangeTitleAction = {
  type: "changeTitle";
  title: string;
};

type ChangePointsEnabledAction = {
  type: "changePointsEnabled";
  pointsEnabled: boolean;
};

type ChangeChartSubtypeAction = {
  type: "changeChartSubtype";
  chartSubtype: ChartSubtype;
};

type ChangeTagOnSeriesAction = {
  type: "changeTagOnSeries";
  seriesId: string;
  title: string;
  tagId: number | null;
  tagName: string | null;
  machineId: number | null;
};

type ChangeMachineOnSeriesAction = {
  type: "changeMachineOnSeries";
  seriesId: string;
  title: string;
  machineId: number | null;
  tagId: number | null;
};

type ChangeXAxesTitleEnabledAction = {
  type: "changeXAxesTitleEnabled";
  xAxisEnabled: boolean;
};

type ToggleYAxisLabelAction = {
  type: "toggleYAxisLabel";
  yAxisId: string;
};

type ChangeXAxesMinAction = {
  type: "changeXAxesMin";
  min?: string;
};
type ChangeXAxesMaxAction = { type: "changeXAxesMax"; max?: string };

type AddSeriesAction = {
  type: "addSeries";
  seriesId: string;
  pointsEnabled: boolean;
};

type AddSiteAverageAction = {
  type: "addSiteAverage";
  series: LineSeries<ScadaMedianDataSource>;
};

type AddSiteRangeAction = {
  type: "addSiteRange";
  series: RangeSeries<ScadaRangeDataSource>;
};

type UpdateSiteAverageAction = {
  type: "updateSiteAverage";
  seriesId: string;
  title: string;
  siteId: number;
  tagId: number;
};

type UpdateSiteRangeAction = {
  type: "updateSiteRange";
  seriesId: string;
  title: string;
  siteId: number;
  tagId: number;
};

type DeleteMultipleSeriesAction = {
  type: "deleteMultipleSeries";
  seriesIds: string[];
};

type DeleteAllSeriesAction = {
  type: "deleteAllSeries";
};

type ChangeYAxisMinAction = {
  type: "changeYAxisMin";
  yAxisId: string;
  min: number | null;
};

type ChangeYAxisMaxAction = {
  type: "changeYAxisMax";
  yAxisId: string;
  max: number | null;
};

type AddReferenceLineAction = {
  type: "addReferenceLine";
  newId: string;
};

type DeleteReferenceLineAction = {
  type: "deleteReferenceLine";
  referenceLineId: string;
};

type ChangeReferenceLineAxisAction = {
  type: "changeReferenceLineAxis";
  selectedReferenceLineId: string | null;
  axis: Axis | null;
};

type ChangeReferenceLineValueAction = {
  type: "changeReferenceLineValue";
  selectedReferenceLineId: string | null;
  value: number | string | Date | null;
};

type ChangeReferenceLineTitleAction = {
  type: "changeReferenceLineTitle";
  referenceLine: ReferenceLine;
  title: string | null;
};

type ResetConfigureMenuAction = {
  type: "resetConfigureMenu";
};

type ResetXAxesRange = {
  type: "resetXAxesRange";
};

type ResetChartAction = {
  type: "resetChart";
  chart: DataExplorationChart;
};

export type ConfigurableChartAction =
  | ChangeTitleAction
  | ChangePointsEnabledAction
  | ChangeChartSubtypeAction
  | ChangeTagOnSeriesAction
  | ChangeMachineOnSeriesAction
  | ChangeXAxesMinAction
  | ChangeXAxesMaxAction
  | ChangeXAxesTitleEnabledAction
  | ToggleYAxisLabelAction
  | AddSeriesAction
  | AddSiteAverageAction
  | AddSiteRangeAction
  | UpdateSiteAverageAction
  | UpdateSiteRangeAction
  | DeleteMultipleSeriesAction
  | DeleteAllSeriesAction
  | ChangeYAxisMinAction
  | ChangeYAxisMaxAction
  | AddReferenceLineAction
  | DeleteReferenceLineAction
  | ChangeReferenceLineAxisAction
  | ChangeReferenceLineValueAction
  | ResetConfigureMenuAction
  | ResetXAxesRange
  | ResetChartAction
  | ChangeReferenceLineTitleAction;
//endregion

//region Action functions
const changeTitle = (
  prevState: DataExplorationChart,
  action: ChangeTitleAction
): DataExplorationChart => {
  return { ...prevState, title: action.title };
};

// deleteAllSeries

const deleteAllSeries = (
  prevState: DataExplorationChart
): DataExplorationChart => {
  return { ...prevState, series: [], yAxes: [] };
};

/**
 * Changes each line series in the chart to use have the passed in points enabled value
 */
const changePointsEnabled = (
  prevState: DataExplorationChart,
  action: ChangePointsEnabledAction
): DataExplorationChart => {
  return {
    ...prevState,
    series: prevState.series.map<DataExplorationSeries>(series => {
      if (series.seriesType === "line") {
        return {
          ...series,
          pointsEnabled: action.pointsEnabled
        };
      } else {
        return series;
      }
    })
  };
};

const updateSpecificDataSourceArgs = (
  prevDataSource: ScadaDatetimeTagDataSource,
  newDataSourceArgs: Partial<ScadaDatetimeTagArgs>
): ScadaDatetimeTagDataSource => {
  return {
    ...prevDataSource,
    args: { ...prevDataSource.args, ...newDataSourceArgs }
  };
};

const updateSelectedSeries = (
  prevSeries: DataExplorationSeries[],
  seriesId: string,
  newDataSourceArgs: Partial<ScadaDatetimeTagArgs>,
  updatedTitle?: string,
  newYAxis?: string
): DataExplorationSeries[] => {
  const newSeries = [...prevSeries];
  const selectedSeriesIndex = newSeries.findIndex(
    x => x.seriesType === "line" && x.id === seriesId
  );

  if (selectedSeriesIndex !== -1) {
    const selectedSeries = newSeries[selectedSeriesIndex] as LineSeries<
      ScadaDatetimeTagDataSource
    >;
    const updatedSelectedSeries = {
      ...selectedSeries,
      title: updatedTitle ? updatedTitle : selectedSeries.title,
      dataSource: updateSpecificDataSourceArgs(
        selectedSeries.dataSource,
        newDataSourceArgs
      ),
      yAxisId: newYAxis || selectedSeries.yAxisId
    };
    newSeries.splice(selectedSeriesIndex, 1, updatedSelectedSeries);
  }

  // If no machine or tag selected, delete child threshold series
  const childSeriesIds = newSeries
    .filter(series => series.parentId === seriesId)
    .map(series => series.id);

  childSeriesIds.forEach(seriesId => {
    deleteObjectById(seriesId, newSeries);
  });

  return newSeries;
};

const changeTagOnSeries = (
  prevState: DataExplorationChart,
  action: ChangeTagOnSeriesAction
): DataExplorationChart => {
  const selectedSeries = getSelectableSeriesById(
    action.seriesId,
    prevState.series
  );

  let newReferenceLines = [...prevState.plotLines];

  if (selectedSeries) {
    /** Check if there is a Y-axis match with the new tag already,
    Set the series `yAxisId` as the matched axis and
    delete original Y-axis if the one is not in used. */

    // Check if the axis already exist for the target tag
    let existingYAxis = prevState.yAxes.find(
      axis =>
        axis.tagId &&
        axis.tagId === action.tagId &&
        axis.id !== selectedSeries.yAxisId
    );
    // Check if the original axis is using by another series
    const isOriginalAxisInUse = prevState.series.some(
      series =>
        series.yAxisId === selectedSeries.yAxisId &&
        series.id !== selectedSeries.id
    );

    let formattedYAxes = [...prevState.yAxes];
    if (existingYAxis) {
      /* If the axis already exist, check the series if there are series
      currently using the original axis, or remove it */
      if (!isOriginalAxisInUse) {
        /* Remove an axis what current series had */
        removeFirstOccurrence(
          formattedYAxes,
          axis => axis.id === selectedSeries.yAxisId
        );
      }
    } else if (isOriginalAxisInUse) {
      /* If there is no exist axis for the tag, but original is in use
      create a new axis and make it a new series axis */
      const { yAxis, yAxes } = addYAxisToArray(
        prevState.yAxes,
        { text: action.tagName || "Y Axis", enabled: true },
        undefined,
        action.tagId
      );
      existingYAxis = yAxis;
      formattedYAxes = yAxes;
    } else {
      /* If there is no exist axis for the tag,
      maintain the current axis and change the name */
      formattedYAxes = prevState.yAxes.map(el => {
        const axis = { ...el };
        if (axis.id === selectedSeries.yAxisId) {
          axis.title.text = action.tagName || "Y Axis";
          axis.tagId = action.tagId;

          /** Change Reference Line title also if the changed axis is the target */
          const isPlotRelatedAxis = newReferenceLines.some(
            plot => plot.targetAxis === el.id
          );
          if (isPlotRelatedAxis) {
            newReferenceLines = newReferenceLines.map(line => {
              const referenceLine = { ...line };
              if (referenceLine.targetAxis === el.id) {
                referenceLine.title = action.tagName || "Y Axis";
              }
              return referenceLine;
            });
          }
        }
        return axis;
      });
    }

    const updatedSeries = updateSelectedSeries(
      prevState.series,
      selectedSeries.id,
      { tagId: action.tagId },
      action.title,
      existingYAxis?.id || undefined
    );

    return {
      ...prevState,
      yAxes: formattedYAxes,
      series: updatedSeries,
      plotLines: newReferenceLines
    };
  } else {
    return prevState;
  }
};

const changeMachineOnSeries = (
  prevState: DataExplorationChart,
  action: ChangeMachineOnSeriesAction
): DataExplorationChart => {
  const selectedSeries = getSelectableSeriesById(
    action.seriesId,
    prevState.series
  );
  const selectedParameterId = action.machineId === null ? null : action.tagId;
  if (selectedSeries) {
    let formattedYAxes = [...prevState.yAxes];
    if (action.machineId === null) {
      formattedYAxes = prevState.yAxes.map(el => {
        const axis = { ...el };
        if (axis.id === selectedSeries.yAxisId) {
          axis.title.text = "Y Axis";
        }
        return axis;
      });
    }

    const updatedSeries = updateSelectedSeries(
      prevState.series,
      selectedSeries.id,
      {
        machineId: action.machineId,
        tagId: selectedParameterId
      },
      action.title
    );

    return {
      ...prevState,
      series: updatedSeries,
      yAxes: formattedYAxes
    };
  } else {
    return prevState;
  }
};

/** Change the minimum value of all x axes on the chart */
const changeXAxesMin = (
  prevState: DataExplorationChart,
  action: ChangeXAxesMinAction
): DataExplorationChart => {
  return {
    ...prevState,
    xAxes: prevState.xAxes.map(axis => {
      return { ...axis, min: action.min?.toString() };
    })
  };
};

/** Change the maximum value of all x axes on the chart */
const changeXAxesMax = (
  prevState: DataExplorationChart,
  action: ChangeXAxesMaxAction
): DataExplorationChart => {
  return {
    ...prevState,
    xAxes: prevState.xAxes.map(axis => {
      return { ...axis, max: action.max?.toString() };
    })
  };
};

const changeXAxesTitleEnabled = (
  prevState: DataExplorationChart,
  action: ChangeXAxesTitleEnabledAction
): DataExplorationChart => {
  return {
    ...prevState,
    xAxes: prevState.xAxes.map(axis => {
      return {
        ...axis,
        title: { text: axis.title.text, enabled: action.xAxisEnabled }
      };
    })
  };
};

const toggleYAxisLabel = (
  prevState: DataExplorationChart,
  action: ToggleYAxisLabelAction
): DataExplorationChart => {
  const newYAxes = [...prevState.yAxes];
  const index = newYAxes.findIndex(el => el.id === action.yAxisId);
  if (index !== -1) {
    const prevYAxis = newYAxes[index];
    const updatedYAxis = {
      ...prevYAxis,
      title: {
        ...prevYAxis.title,
        enabled: !prevYAxis.title.enabled
      }
    };

    newYAxes[index] = updatedYAxis;
  }

  if (action.yAxisId) {
    return {
      ...prevState,
      yAxes: newYAxes
    };
  } else {
    return prevState;
  }
};

/**
 * Creates a copy of the previous y-axis array with a new y-axis appended
 * @param yAxes the previous y-axis array
 * @param title the title of the new y-axis
 * @param type the type of the new y-axis
 * @param tagId the target Tag(Parameter) id
 * @returns the newly created y-axis and the list containing the previous and new y-axes
 */
const addYAxisToArray = (
  yAxes: YAxis[],
  title?: { text: string; enabled: boolean },
  type?: "linear" | "datetime",
  tagId?: number | null
): { yAxis: YAxis; yAxes: YAxis[] } => {
  const list = [...yAxes];
  let yAxis: YAxis;
  const existDefaultAxis = list.find(axis => !axis.tagId);
  if (existDefaultAxis && !tagId) {
    yAxis = existDefaultAxis;
  } else {
    yAxis = {
      alignment: list.length > 0 ? "right" : "left",
      id: createUUID(),
      title: title || { text: "Y axis", enabled: true },
      type: type || "linear",
      tagId: tagId || null
    };
    list.push(yAxis);
  }
  return { yAxis, yAxes: list };
};

const addSeries = (
  prevState: DataExplorationChart,
  action: AddSeriesAction
): DataExplorationChart => {
  const { yAxis, yAxes } = addYAxisToArray(prevState.yAxes);
  const newSeries: LineSeries<ScadaDatetimeTagDataSource> = {
    id: action.seriesId,
    seriesType: "line",
    title: "...",
    colorIndex: getColorIndex(prevState.series, COLOR_PALETTE.length),
    pointsEnabled: action.pointsEnabled,
    xAxisId: prevState.xAxes[0].id,
    yAxisId: yAxis.id,
    dataSource: {
      type: "scada-datetime-tag",
      args: {
        tagId: null,
        machineId: null,
        filters: {}
      }
    }
  };

  return {
    ...prevState,
    yAxes,
    series: [...prevState.series, newSeries]
  };
};

const addSiteAverage = (
  prevState: DataExplorationChart,
  action: AddSiteAverageAction
): DataExplorationChart => {
  return {
    ...prevState,
    series: [...prevState.series, action.series]
  };
};

const addSiteRange = (
  prevState: DataExplorationChart,
  action: AddSiteRangeAction
): DataExplorationChart => {
  return {
    ...prevState,
    series: [...prevState.series, action.series]
  };
};

const updateSiteAverage = (
  prevState: DataExplorationChart,
  action: UpdateSiteAverageAction
): DataExplorationChart => {
  const series = [...prevState.series];
  const siteAverageIndex = series.findIndex(
    x => x.id === action.seriesId && x.dataSource.type === "scada-median"
  );

  if (siteAverageIndex !== -1) {
    const siteAverage: LineSeries<ScadaMedianDataSource> = {
      ...(series[siteAverageIndex] as LineSeries<ScadaMedianDataSource>)
    };
    siteAverage.title = action.title;
    siteAverage.dataSource.args.siteId = action.siteId;
    siteAverage.dataSource.args.tagId = action.tagId;
    series.splice(siteAverageIndex, 1, siteAverage);
  }

  return { ...prevState, series };
};

const updateSiteRange = (
  prevState: DataExplorationChart,
  action: UpdateSiteRangeAction
): DataExplorationChart => {
  const series = [...prevState.series];
  const siteRangeIndex = series.findIndex(
    x => x.id === action.seriesId && x.dataSource.type === "scada-range"
  );

  if (siteRangeIndex !== -1) {
    const siteRange: RangeSeries<ScadaRangeDataSource> = {
      ...(series[siteRangeIndex] as RangeSeries<ScadaRangeDataSource>)
    };
    siteRange.title = action.title;
    siteRange.dataSource.args.siteId = action.siteId;
    siteRange.dataSource.args.tagId = action.tagId;
    series.splice(siteRangeIndex, 1, siteRange);
  }

  return { ...prevState, series };
};

const deleteObjectById = <T extends { id: string }>(
  id: string,
  arrayToUpdate: T[]
): void => {
  removeFirstOccurrence(arrayToUpdate, x => x.id === id);
};

/**
 * Creates a new array of all the y axes that are still in use
 * @param prevYAxes the y axes that existed previously in the chart
 * @param series the array of series that are currently in the chart
 */
const getActiveYAxes = (
  prevYAxes: YAxis[],
  series: DataExplorationSeries[]
): YAxis[] => {
  return prevYAxes.filter(yAxis => series.some(s => s.yAxisId === yAxis.id));
};

const deleteSeries = <T extends DataExplorationSeries>(
  seriesId: string,
  seriesArray: T[]
): void => {
  deleteObjectById(seriesId, seriesArray);

  // Recursively delete any child series
  const childSeriesIds = seriesArray
    .filter(x => x.parentId === seriesId)
    .map(x => x.id);
  childSeriesIds.forEach(childSeriesId =>
    deleteSeries(childSeriesId, seriesArray)
  );
};

/**
 * Deletes multiple series given a list of series ids
 * @param seriesIds the series ids of each series to delete
 * @param seriesArray the array of series to delete from
 */
const deleteMultipleSeriesByIds = <T extends DataExplorationSeries>(
  seriesIds: string[],
  seriesArray: T[]
): void => {
  // remove the series
  seriesIds.forEach(seriesId => {
    deleteSeries(seriesId, seriesArray);
  });
};

const deleteMultipleSeries = (
  prevState: DataExplorationChart,
  action: DeleteMultipleSeriesAction
): DataExplorationChart => {
  const series = [...prevState.series];

  deleteMultipleSeriesByIds(action.seriesIds, series);

  // Need to find which y axes are still active, after deleting the series and any linked series
  const yAxes = getActiveYAxes(prevState.yAxes, series);

  return {
    ...prevState,
    series,
    yAxes
  };
};

const changeYAxisMin = (
  prevState: DataExplorationChart,
  action: ChangeYAxisMinAction
): DataExplorationChart => {
  const newYAxes = [...prevState.yAxes];
  const index = newYAxes.findIndex(el => el.id === action.yAxisId);

  if (index !== -1) {
    let newYAxis = {
      ...prevState.yAxes[index],
      min: action.min
    };
    newYAxes[index] = newYAxis;
  }

  return { ...prevState, yAxes: newYAxes };
};

const changeYAxisMax = (
  prevState: DataExplorationChart,
  action: ChangeYAxisMaxAction
): DataExplorationChart => {
  const newYAxes = [...prevState.yAxes];
  const index = newYAxes.findIndex(el => el.id === action.yAxisId);

  if (index !== -1) {
    let newYAxis = {
      ...prevState.yAxes[index],
      max: action.max
    };
    newYAxes[index] = newYAxis;
  }
  return { ...prevState, yAxes: newYAxes };
};

const resetXAxesRange = (
  prevState: DataExplorationChart
): DataExplorationChart => {
  return {
    ...prevState,
    xAxes: prevState.xAxes.map(axis => {
      return { ...axis, min: undefined, max: undefined };
    })
  };
};

const addReferenceLine = (
  prevState: DataExplorationChart,
  action: AddReferenceLineAction
): DataExplorationChart => {
  const newReferenceLine: ReferenceLine = {
    id: action.newId,
    targetAxis: null,
    title: "...",
    color: "black",
    dashStyle: "dash",
    value: null,
    width: 2
  };

  return {
    ...prevState,
    plotLines: [...prevState.plotLines, newReferenceLine]
  };
};

const deleteReferenceLine = (
  prevState: DataExplorationChart,
  action: DeleteReferenceLineAction
): DataExplorationChart => {
  const filteredReferenceLines = prevState.plotLines?.filter(
    referenceLine => referenceLine.id !== action.referenceLineId
  );
  return {
    ...prevState,
    plotLines: filteredReferenceLines
  };
};

const changeReferenceLineAxis = (
  prevState: DataExplorationChart,
  action: ChangeReferenceLineAxisAction
): DataExplorationChart => {
  const prevReferenceLines = prevState.plotLines
    ? [...prevState.plotLines]
    : [];
  const targetIndex = prevReferenceLines.findIndex(
    referenceLine => referenceLine.id === action.selectedReferenceLineId
  );
  if (targetIndex !== -1) {
    prevReferenceLines[targetIndex].targetAxis = action.axis
      ? action.axis.id
      : null;
    prevReferenceLines[targetIndex].title = action.axis?.title?.text
      ? action.axis.title.text
      : "...";
  }
  return {
    ...prevState,
    plotLines: prevReferenceLines
  };
};

const changeReferenceLineValue = (
  prevState: DataExplorationChart,
  action: ChangeReferenceLineValueAction
): DataExplorationChart => {
  const prevReferenceLines = prevState.plotLines
    ? [...prevState.plotLines]
    : [];
  const targetIndex = prevReferenceLines.findIndex(
    referenceLine => referenceLine.id === action.selectedReferenceLineId
  );
  if (targetIndex !== -1) {
    prevReferenceLines[targetIndex].value = action.value;
  }
  return {
    ...prevState,
    plotLines: prevReferenceLines
  };
};

const changeReferenceLineTitle = (
  prevState: DataExplorationChart,
  action: ChangeReferenceLineTitleAction
): DataExplorationChart => {
  const prevReferenceLines = [...prevState.plotLines];
  const targetIndex = prevReferenceLines.findIndex(
    referenceLine => referenceLine.id === action.referenceLine.id
  );
  if (targetIndex !== -1) {
    if (!action.title || action.title === "") {
      // When user remove the title, set as Axis title as default
      const targetAxis = [...prevState.xAxes, ...prevState.yAxes].find(
        axis => axis.id === action.referenceLine.targetAxis
      );
      prevReferenceLines[targetIndex].title =
        targetAxis?.title?.text || "Reference";
    } else {
      prevReferenceLines[targetIndex].title = action.title;
    }
  }
  return {
    ...prevState,
    plotLines: prevReferenceLines
  };
};
//endregion

//region helper functions
/**
 * Takes in a series and determines if it is a selectable by the user based on the type of series
 * @param series The series to check
 */
export const isSelectableSeries = (
  series: Series<DataSource>
): series is LineSeries<ScadaDatetimeTagDataSource> => {
  return (
    series.seriesType === "line" &&
    isScadaDateTimeTagDataSource(series.dataSource)
  );
};

/**
 * Finds the series with the given id from any selectable series in the array provided
 * @param id the id of the series
 * @param series an array of series
 * @returns a series object or null if not found
 */
const getSelectableSeriesById = (
  id: string | null,
  series: DataExplorationSeries[]
): LineSeries<ScadaDatetimeTagDataSource> | null => {
  if (id === null) {
    return null;
  }

  return series.filter(isSelectableSeries).find(x => x.id === id) ?? null;
};
//endregion

//region Reducer
export const configurableChartReducer = (
  state: DataExplorationChart,
  action: ConfigurableChartAction
): DataExplorationChart => {
  switch (action.type) {
    case "changeTitle":
      return changeTitle(state, action);
    case "changePointsEnabled":
      return changePointsEnabled(state, action);
    case "changeTagOnSeries":
      return changeTagOnSeries(state, action);
    case "changeMachineOnSeries":
      return changeMachineOnSeries(state, action);
    case "changeXAxesTitleEnabled":
      return changeXAxesTitleEnabled(state, action);
    case "toggleYAxisLabel":
      return toggleYAxisLabel(state, action);
    case "changeXAxesMin":
      return changeXAxesMin(state, action);
    case "changeXAxesMax":
      return changeXAxesMax(state, action);
    case "addSeries":
      return addSeries(state, action);
    case "addSiteAverage":
      return addSiteAverage(state, action);
    case "addSiteRange":
      return addSiteRange(state, action);
    case "updateSiteAverage":
      return updateSiteAverage(state, action);
    case "updateSiteRange":
      return updateSiteRange(state, action);
    case "deleteMultipleSeries":
      return deleteMultipleSeries(state, action);
    case "deleteAllSeries":
      return deleteAllSeries(state);
    case "changeYAxisMin":
      return changeYAxisMin(state, action);
    case "changeYAxisMax":
      return changeYAxisMax(state, action);
    case "addReferenceLine":
      return addReferenceLine(state, action);
    case "deleteReferenceLine":
      return deleteReferenceLine(state, action);
    case "changeReferenceLineAxis":
      return changeReferenceLineAxis(state, action);
    case "changeReferenceLineValue":
      return changeReferenceLineValue(state, action);
    case "changeReferenceLineTitle":
      return changeReferenceLineTitle(state, action);
    case "resetXAxesRange":
      return resetXAxesRange(state);
    case "resetChart":
      return action.chart;
    default:
      throw new Error("Unhandled action in chart reducer");
  }
};
//endregion

export const useConfigurableChartReducer = (
  chart: DataExplorationChart
): [DataExplorationChart, Dispatch<ConfigurableChartAction>] => {
  const [state, dispatch] = useReducer(configurableChartReducer, chart);

  useEffect(() => {
    dispatch({ type: "resetChart", chart });
  }, [chart]);

  return [state, dispatch];
};
